import React, { useEffect, useRef, useState } from "react"
import {
  withPropsOnChange,
  compose,
  withProps,
  withStateHandlers,
  defaultProps,
  withState,
  branch,
} from "recompact"
import { connect } from "react-redux"
import { change, Field, getFormValues } from "redux-form"
import get from "lodash/get"
import difference from "lodash/difference"
import moment from "moment-timezone"
import entityApi from "src/hocs/entity"
import { entityItem } from "src/engine/modules/entity/selectors"
import eventModel from "src/models/event"
import planModel from "src/models/plan"
import eventResources from "src/hocs/eventResources"
import LoaderBox from "src/components/forms/fragments/LoaderBox"
import { reduxForm } from "src/engine/modules/entity/plugins"
import { selectStatusAvailable } from "src/engine/modules/features/selectors"
import { selectCurrentList } from "src/engine/modules/lists/selectors"
import { readList } from "src/engine/modules/lists/actions/creators/read"
import FieldSet from "@infotech/uikit-fieldset"
import EventDurationPicker from "src/components/forms/EventDurationPicker"
import { Flex, Box } from "@infotech/uikit-grid"
import Input from "src/components/Input"
import withQueryParams from "src/hocs/withQueryParams"
import allEventTypes from "src/hocs/allEventTypes"
import allPlans from "src/hocs/allPlans"
import InfinitySelect from "src/components/Selects/InfinitySelect"
import Checkbox from "@infotech/uikit-checkbox"
// widget imports
import EventTypeSelect from "src/components/forms/EventTypeSelect"

import ExternalLink from "src/components/ExternalLink"
import makeNewEventInterval from "src/helpers/makeNewEventInterval"
import Textarea from "src/components/Textarea"
import storage from "src/helpers/storage"
import cn from "classnames"
import withSideModalPending from "src/hocs/withSideModalPending"
import StatusSelect from "src/components/Selects/StatusSelect"
import EventConnectedInfo from "src/widgets/EventConnectedInfo"
import getEventType from "./hocs/getEventType"
import styles from "./styles.css"
import { EVENT_CREATION_INITIAL_PARAMS } from "../../Calendar/constants"
import { LIST_NAME, getBasicUrl } from "./helper"
import Resources from "./Resources"
import ResourceNewForm from "./Resources/ResourceNewForm"

export const FORM_NAME = `createEvent`
const selectValues = getFormValues(FORM_NAME)
const getCurrentTime = () =>
  moment()
    .set({
      second: 0,
      millisecond: 0,
    })
    .valueOf()
const getTestAttribute = name => `planner-events-item-${name}`
const getEventTypeById = (eventTypes, id) =>
  eventTypes.find(element => element.id === id)

const enhance = compose(
  withQueryParams(),

  // Pulls the initialParams from local storage
  withState(`initialParams`, null, () => {
    const { resourceUuid, startDate, endDate, eventTypeId, name } =
      storage.get(EVENT_CREATION_INITIAL_PARAMS) || {}
    storage.remove(EVENT_CREATION_INITIAL_PARAMS)

    const hasAllInitialParams =
      resourceUuid && startDate && endDate && eventTypeId && name

    return hasAllInitialParams
      ? {
          resourceUuid,
          startDate,
          endDate,
          eventTypeId,
          name,
        }
      : null
  }),
  withProps(props => ({
    form: FORM_NAME,
    enableReinitialise: false,
    onSubmit(
      data,
      dispatch,
      {
        event: {
          api: { save },
        },
      },
    ) {
      const {
        planId,
        match: {
          params: { id },
        },
      } = props
      const isNew = id === `new`
      const { resourceUuids, ...withoutRes } = data
      const saveData = isNew ? data : withoutRes
      return save({
        ...saveData,
        planId: planId ? parseInt(planId, 10) : undefined,
        eventTypeId: get(data, `eventTypeId.value`) || data.eventTypeId,
      })
    },
  })),
  connect((state, { match: { params: { planId } } }) => ({
    eventTypeId: get(selectValues(state), `eventTypeId`),
    formValues: selectValues(state),
    statusFeature: selectStatusAvailable(state),
    currentPlan: entityItem(state, planModel, planId),
  })),
  getEventType,
  entityApi({
    to: `event`,
    model: eventModel,
    plugins: reduxForm(
      compose(
        defaultProps({
          validate: ({ startDateTime, endDateTime }, { eventType }) => {
            const errors = {}
            if (startDateTime === endDateTime) {
              errors.endDateTime = `Дата окончания события должна быть заполнена`
            } else if (startDateTime > endDateTime) {
              errors.startDateTime = `Время начала не должно быть больше времени конца`
            }
            if (get(eventType, `data.onlyDate`)) {
              const isStartOnDayZeroPoint =
                moment(startDateTime)
                  .startOf(`day`)
                  .valueOf() === moment(startDateTime).valueOf()
              const isEndOnDayZeroPoint =
                moment(endDateTime)
                  .startOf(`day`)
                  .valueOf() === moment(endDateTime).valueOf()
              if (!isStartOnDayZeroPoint) {
                errors.startDateTime = `Задана опция "Использовать только дни". Событие должно начинаться только в 00:00`
              }
              if (!isEndOnDayZeroPoint) {
                errors.endDateTime = `Задана опция "Использовать только дни". Событие должно заканчиваться только в 00:00`
              }
            }
            return errors
          },
        }),
        withPropsOnChange(
          ({ initialValues }, { initialValues: nextInitialValues }) => {
            const negate =
              !initialValues ||
              !initialValues.startDateTime ||
              !initialValues.endDateTime
            return initialValues !== nextInitialValues && negate
          },
          props => {
            const {
              initialValues,
              initialParams,
              eventType,
              currentPlan,
            } = props
            /* We should define default date and resources on initial stage if we have initialParams */
            if (
              !initialValues ||
              !initialValues.startDateTime ||
              !initialValues.endDateTime
            ) {
              return {
                initialValues: initialParams
                  ? {
                      ...initialValues,
                      resourceUuids: [initialParams.resourceUuid],
                      eventTypeId: initialParams.eventTypeId,
                      startDateTime: moment(initialParams.startDate),
                      endDateTime: moment(initialParams.endDate),
                      name: initialParams.name,
                      planUuid: initialParams.planUuid,
                    }
                  : {
                      ...initialValues,
                      startDateTime: getCurrentTime(),
                      endDateTime:
                        getCurrentTime() +
                        (get(eventType, `data.defExecutionTime`) || 86400000),
                      planUuid: get(currentPlan, `data.uuid`),
                    },
              }
            }

            return {}
          },
        ),
      ),
    ),
  }),
  withProps(
    props =>
      get(props, `event.data.planInfo`) ||
      (get(props, `formValues.planUuid`) && {
        planId:
          get(props, `event.data.planInfo.id`) ||
          get(props, `formValues.planUuid`),
      }),
  ),
  // Update form pending state with event state
  withPropsOnChange(
    (props, nextProps) =>
      props.event &&
      nextProps.event &&
      props.event.pending !== nextProps.event.pending,
    ({ event: { pending }, setPendingState }) => {
      if (setPendingState && typeof setPendingState === `function`) {
        setPendingState(pending)
      }
      return null
    },
  ),
  // Form with initial params is already dirty
  // It is needed to unlock the submit button
  withProps(({ initialParams, workspace }) => {
    if (initialParams && !workspace.labels.dirty) {
      workspace.addLabel(`dirty`, 1)
    }
  }),
  connect(
    state => ({
      startDateTime: get(selectValues(state), `startDateTime`),
      endDateTime: get(selectValues(state), `endDateTime`),
      eventName: get(selectValues(state), `name`),
      planUuidFormValue: get(selectValues(state), `planUuid`),
      planEventTypes: selectCurrentList(state, LIST_NAME),
    }),
    {
      readList,
    },
  ),
  withStateHandlers(
    {
      cumulative: true,
      planQuery: {
        limit: 30,
        offset: 0,
        query: `locked==false`,
      },
      eventTypeQuery: {
        limit: 30,
        offset: 0,
      },
    },
    {
      changePlansQuery: () => newQuery => ({
        planQuery: newQuery,
      }),
      changeEventTypeQuery: () => newQuery => ({
        eventTypeQuery: newQuery,
      }),
    },
  ),
  allEventTypes(({ eventTypeQuery }) => ({
    to: `eventTypes`,
    query: eventTypeQuery,
  })),
  allPlans(({ planQuery }) => ({
    to: `planList`,
    query: planQuery,
  })),
  // == widget logic
  withProps(({ event }) => ({ eventUuid: get(event, `initialId`) })),
  branch(
    ({ eventUuid, isVirtual }) => !eventUuid || isVirtual,
    Component => Component,
    eventResources(),
  ),
  // ==
  connect(null, (dispatch, props) => ({
    changeEventTypeId: (option, eventTypeRef) => {
      const {
        eventName,
        eventTypeId,
        eventTypes: { data: eventTypesData },
        startDateTime,
        endDateTime,
      } = props
      const value = get(option, `value`, null)
      // Изменение самого поля
      dispatch(change(FORM_NAME, `eventTypeId`, value))
      if (value) {
        // TODO
        // eslint-disable-next-line no-param-reassign
        eventTypeRef.current = eventTypeRef.current.concat({
          id: value,
          name: get(option, `label`),
          color: get(option, `color`),
        })
      }

      const eventTypeOld = getEventTypeById(eventTypesData, eventTypeId)
      const eventTypeNew = getEventTypeById(eventTypesData, value)

      // Смена имени события
      if (
        (eventTypeOld && (eventTypeOld.name === eventName || !eventName)) ||
        !eventTypeOld
      ) {
        dispatch(
          change(FORM_NAME, `name`, eventTypeNew ? eventTypeNew.name : ``),
        )
      }

      const [newStartTime, newEndTime] = makeNewEventInterval({
        startTime: startDateTime,
        endTime: endDateTime,
        isDateOnly: get(eventTypeNew, `onlyDate`),
        defaultInterval: get(eventTypeNew, `defExecutionTime`),
      })

      if (newStartTime !== startDateTime) {
        dispatch(change(FORM_NAME, `startDateTime`, newStartTime))
      }

      if (newEndTime !== endDateTime) {
        dispatch(change(FORM_NAME, `endDateTime`, newEndTime))
      }
    },
    // При изменении даты начала, соответственно меняется дата окончания
    changeStartDateTime: value => {
      const { startDateTime, endDateTime } = props
      const difference = endDateTime - startDateTime
      dispatch(change(FORM_NAME, `startDateTime`, moment(value)))
      dispatch(change(FORM_NAME, `endDateTime`, value + difference))
    },
    // При изменении длительности, соответственно меняется дата окончания
    durationChange: value => {
      const { startDateTime } = props
      dispatch(
        change(FORM_NAME, `endDateTime`, moment(startDateTime + value * 1000)),
      )
    },
    planUuidChange: value => dispatch(change(FORM_NAME, `planUuid`, value)),
    eventTypeChange: value => dispatch(change(FORM_NAME, `eventTypeId`, value)),
  })),
  withStateHandlers(
    { isResourcesTableView: false },
    {
      toggleResourcesView: ({ isResourcesTableView }) => () => ({
        isResourcesTableView: !isResourcesTableView,
      }),
    },
  ),
  withProps(
    ({
      match,
      canEdit = () => true,
      canChangeStatus = () => true,
      event: { data },
    }) => {
      const isNew = match.params.id === `new`
      return {
        isStatusDisabled: !canChangeStatus(data) || get(data, `locked`),
        isDisabled: !canEdit(data) && !isNew,
        isNew,
      }
    },
  ),
  withSideModalPending(`event`, `Событие не найдено`, true),
)

export default enhance(
  ({
    handleSubmit,
    event: { id, pending, data },
    eventType,
    startDateTime,
    endDateTime,
    isResourcesTableView,
    initialParams,
    initialValues,
    match,
    isDisabled,
    changeEventTypeId,
    eventTypes,
    changeStartDateTime,
    durationChange,
    planList,
    changePlansQuery,
    changeEventTypeQuery,
    planUuidChange,
    eventTypeChange,
    planUuidFormValue,
    statusFeature,
    isNew,
    formValues,
    currentPlan,
    planEventTypes,
    readList,
    planQuery,
    eventTypeQuery,
    eventResources,
    isStatusDisabled,
  }) => {
    const [fromPlan, changeOptionsFrom] = useState(
      Boolean(match.params.planId && isNew && !initialParams),
    )
    useEffect(() => {
      if (planUuidFormValue && fromPlan) {
        readList({
          basicUrl: getBasicUrl(planUuidFormValue),
          name: LIST_NAME,
          query: eventTypeQuery,
        })
      }
    }, [planUuidFormValue, eventTypeQuery, fromPlan])

    const isNewEventFromPlan =
      match.params.planId && isNew && get(currentPlan, `data.uuid`)

    const statusAvailable = !isNew && statusFeature
    const uuid = get(data, `uuid`)
    const status = get(data, `status`)
    const restrictions = get(data, `restrictions`)

    const planSelectDisabled = planList.pending

    const eventPlanUuid = get(data, `planUuid`)
    const eventPlanName = get(data, `planName`)
    // TODO: подумать над вынесением логики useRef в InfinitySelect
    const planRef = useRef([])
    useEffect(() => {
      if (planList.data.length) {
        if (eventPlanUuid) {
          planRef.current = planList.data.concat({
            uuid: eventPlanUuid,
            name: eventPlanName,
          })
        } else if (isNewEventFromPlan) {
          planRef.current = planList.data.concat({
            uuid: get(currentPlan, `data.uuid`),
            name: get(currentPlan, `data.name`),
          })
        } else {
          planRef.current = planList.data
        }
      }
    }, [planList.data.length, isNewEventFromPlan, eventPlanUuid])

    const planOptions = planRef.current.map(item => ({
      value: item.uuid,
      label: item.name,
    }))

    const eventTypesOptions = !fromPlan
      ? get(eventTypes, `data`, [])
      : get(planEventTypes, `data`, [])
    const eventTypeLoading = !fromPlan
      ? get(eventTypes, `pending`, false)
      : get(planEventTypes, `loading`, false)

    const eventTypeId = get(data, `eventTypeId`)
    const eventTypeRef = useRef([])
    eventTypeRef.current = !fromPlan
      ? get(eventTypes, `data`, [])
      : get(planEventTypes, `data`, [])
    useEffect(() => {
      if (eventTypesOptions.length) {
        if (eventTypeId) {
          eventTypeRef.current = eventTypesOptions.concat({
            id: eventTypeId,
            name: get(data, `eventTypeName`),
            color: get(data, `eventTypeColor`),
          })
        } else if (
          get(initialParams, `eventTypeId`) &&
          get(initialParams, `name`) &&
          get(initialParams, `eventTypeColor`)
        ) {
          eventTypeRef.current = eventTypesOptions.concat({
            id: get(initialParams, `eventTypeId`),
            name: get(initialParams, `name`),
            color: get(initialParams, `eventTypeColor`),
          })
        } else {
          eventTypeRef.current = eventTypesOptions
        }
      }
    }, [eventTypesOptions.length, get(initialParams, `eventTypeId`), fromPlan])

    const isOnlyDateType = get(eventType, `data.onlyDate`, false)

    const getPlanValue = () => {
      if (planUuidFormValue || planUuidFormValue === null)
        return planUuidFormValue
      if (eventPlanUuid) return eventPlanUuid
      if (isNewEventFromPlan) return isNewEventFromPlan
      return null
    }
    const checkBoxDisabled =
      !isNew ||
      isDisabled ||
      planSelectDisabled ||
      eventTypeLoading ||
      (!getPlanValue() && !fromPlan) ||
      get(restrictions, `eventType`)

    const allowedResourceTypesByEventType = get(
      eventType,
      `data.allowedResourceTypes`,
      [],
    )
    const allowedResourceTypesByStatus = get(
      restrictions,
      `restrictedResourceTypes`,
      [],
    )

    const allowedResourceTypes = difference(
      allowedResourceTypesByEventType,
      allowedResourceTypesByStatus,
    )

    const externalLink = get(data, `externalLink`)

    return (
      <div className={styles[`full-height`]}>
        <form onSubmit={handleSubmit} className={styles[`full-height`]}>
          <Flex className={styles[`full-height-not-column`]}>
            <Box
              className={cn(styles[`left-part`], {
                [styles.hidden]: isResourcesTableView,
              })}
              br
              column
            >
              {statusAvailable && (
                <Box bb pt="2md" pl="2md" pr="2md">
                  <Box mb="-1md">
                    <FieldSet caption="Статус">
                      <StatusSelect
                        status={status}
                        eventData={
                          formValues
                            ? {
                                ...data,
                                name: formValues.name,
                                description: formValues.description,
                                startDateTime: formValues.startDateTime,
                                endDateTime: formValues.endDateTime,
                                eventTypeId: formValues.eventTypeId,
                                planUuid: formValues.planUuid,
                              }
                            : data
                        }
                        eventUuid={uuid}
                        disabled={isDisabled || isStatusDisabled}
                        type="eventForm"
                      />
                    </FieldSet>
                  </Box>
                </Box>
              )}
              <Box className={styles.ColumnContent}>
                <Box pb="2md">
                  <FieldSet caption="План">
                    <div data-testid={getTestAttribute(`planUuid`)}>
                      <Field
                        name="planUuid"
                        component={InfinitySelect}
                        props={{
                          hideSelectedOptions: true,
                          value: getPlanValue(),
                          placeholder: `Выберите план`,
                          isDisabled:
                            planSelectDisabled ||
                            isDisabled ||
                            get(restrictions, `plan`),
                          isClearable: true,
                          isLoading: planSelectDisabled,
                          options: planOptions,
                          infinityConfig: {
                            searchExtraQuery: `locked==false`,
                            searchBy: `name`,
                            changeQuery: changePlansQuery,
                            queryParams: planQuery,
                          },
                          format: null,
                          onChange: option => {
                            const value = get(
                              option,
                              `target.value.value`,
                              null,
                            )
                            planUuidChange(value)
                            if (fromPlan) {
                              if (!value) {
                                changeOptionsFrom(!fromPlan)
                              }
                              eventTypeChange(null)
                            }
                          },
                        }}
                      />
                    </div>
                  </FieldSet>
                </Box>
                <FieldSet caption="Тип события" required>
                  <div data-testid={getTestAttribute(`eventTypeId`)}>
                    <Field
                      component={EventTypeSelect}
                      name="eventTypeId"
                      format={null}
                      props={{
                        isDisabled:
                          isDisabled ||
                          get(restrictions, `eventType`) ||
                          !isNew,
                        isLoading: eventTypeLoading,
                        placeholder: `Выберите тип события`,
                        options: eventTypeRef.current.map(item => ({
                          value: item.id,
                          label: item.name,
                          color: item.color,
                        })),
                      }}
                      onChange={value => changeEventTypeId(value, eventTypeRef)}
                      infinityConfig={{
                        searchExtraQuery: ``,
                        searchBy: `name`,
                        changeQuery: changeEventTypeQuery,
                        queryParams: eventTypeQuery,
                      }}
                    />
                  </div>
                </FieldSet>
                <Box
                  loose
                  pb="1md"
                  data-testid={getTestAttribute(`optionsFrom`)}
                >
                  <Checkbox
                    label="Только из плана"
                    disabled={checkBoxDisabled}
                    checked={fromPlan}
                    onChange={() => {
                      changeOptionsFrom(!fromPlan)
                      eventTypeChange(null)
                    }}
                  />
                </Box>
                <FieldSet caption="Наименование события" required>
                  <div data-testid={getTestAttribute(`name`)}>
                    <Field
                      component={Input}
                      name="name"
                      props={{
                        placeholder: `Введите название события`,
                        disabled: isDisabled || get(restrictions, `name`),
                      }}
                    />
                  </div>
                </FieldSet>
                <EventDurationPicker
                  key={id}
                  getTestAttribute={getTestAttribute}
                  startDateTime={startDateTime}
                  endDateTime={endDateTime}
                  maxExecutionTime={get(eventType, `data.maxExecutionTime`)}
                  minExecutionTime={get(eventType, `data.minExecutionTime`)}
                  eventTypeId={get(eventType, `data.id`)}
                  noError
                  isDisabled={isDisabled}
                  isOnlyDateType={isOnlyDateType}
                  isDisabledStart={get(restrictions, `startDateTime`)}
                  isDisabledEnd={get(restrictions, `endDateTime`)}
                  onStartDateTimeChange={changeStartDateTime}
                  onDurationChange={durationChange}
                  executionTime={(endDateTime - startDateTime) / 1000}
                />
                <FieldSet caption="Описание">
                  <div data-testid={getTestAttribute(`description`)}>
                    <Field
                      component={Textarea}
                      name="description"
                      props={{
                        minRows: 3,
                        className: styles.description,
                        placeholder: `Введите описание`,
                        disabled:
                          isDisabled || get(restrictions, `description`),
                      }}
                    />
                  </div>
                </FieldSet>
                {externalLink && <ExternalLink data={externalLink} />}
              </Box>
            </Box>
            <Box
              className={cn({
                [styles[`right-part`]]: !isResourcesTableView,
                [styles[`right-part-full-width`]]: isResourcesTableView,
              })}
              br
            >
              {!pending &&
              !isNew &&
              !!(eventType && eventType.data) &&
              !!(allowedResourceTypes && allowedResourceTypes.length) ? (
                <Resources
                  isNew={isNew}
                  eventType={!!(eventType && eventType.data)}
                  eventUuid={match.params.id}
                  isDisabled={isDisabled}
                  isResourcesDisabled={get(restrictions, `resources`)}
                  getTestAttribute={getTestAttribute}
                  allowedResourceTypesByStatus={allowedResourceTypesByStatus}
                  allowedResourceTypes={allowedResourceTypes.join(`,`)}
                />
              ) : (
                <ResourceNewForm
                  isNew={isNew}
                  getTestAttribute={getTestAttribute}
                  initialResource={get(initialValues, `resourceUuids`, [])}
                  allowedResourceTypes={allowedResourceTypes || []}
                />
              )}
            </Box>
            {!isNew && (
              <Box w="522px" className={styles.WidgetColumnContent}>
                <EventConnectedInfo
                  eventData={{
                    uuid: get(data, `uuid`),
                    startDateTime: get(data, `startDateTime`),
                    endDateTime: get(data, `endDateTime`),
                  }}
                  resourcesData={get(eventResources, `data`)}
                />
              </Box>
            )}
          </Flex>
        </form>
        {pending && <LoaderBox />}
      </div>
    )
  },
)
